import { useState, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { FolderPlus, Loader2 } from 'lucide-react'
import Modal from '@components/ui/Modal'

interface CreateFolderModalProps {
  isOpen: boolean
  onClose: () => void
  parentFolderId?: string
}

export default function CreateFolderModal({
  isOpen,
  onClose,
  parentFolderId,
}: CreateFolderModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)

  // Auto-focus the name input when modal opens
  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      nameInputRef.current.focus()
    }
  }, [isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      alert(t('folders.errors.name_required'))
      return
    }

    setIsSubmitting(true)

    router.post(
      '/folders',
      {
        folder: {
          name: name.trim(),
          description: description.trim() || undefined,
          parent_id: parentFolderId || undefined,
        },
      },
      {
        except: ['breadcrumbs', 'storage_usage'],
        preserveScroll: true,
        onSuccess: () => {
          handleClose()
        },
        onError: (errors) => {
          console.error('Folder creation error:', errors)
          setIsSubmitting(false)
        },
        onFinish: () => {
          setIsSubmitting(false)
        },
      }
    )
  }

  const handleClose = () => {
    setName('')
    setDescription('')
    setIsSubmitting(false)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      title={t('folders.create_title')}
      icon={<FolderPlus className="w-6 h-6 text-primary" />}
      disabled={isSubmitting}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-ghost"
            disabled={isSubmitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="create-folder-form"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FolderPlus className="w-4 h-4" />
            )}
            {t('folders.create')}
          </button>
        </>
      }
    >
      <form id="create-folder-form" onSubmit={handleSubmit} className="space-y-5">
        {/* Name Field */}
        <div className="form-control">
          <label className="label pb-1">
            <span className="label-text font-medium">
              {t('folders.name')}
              <span className="text-error ml-1">*</span>
            </span>
          </label>
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('folders.name_placeholder')}
            className="input input-bordered w-full focus:input-primary"
            required
            disabled={isSubmitting}
            maxLength={255}
          />
        </div>

        {/* Description Field */}
        <div className="form-control">
          <label className="label pb-1">
            <span className="label-text font-medium">{t('folders.description')}</span>
            <span className="label-text-alt text-base-content/50">
              {t('common.optional')}
            </span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('folders.description_placeholder')}
            className="textarea textarea-bordered w-full focus:textarea-primary resize-none"
            rows={3}
            disabled={isSubmitting}
          />
        </div>
      </form>
    </Modal>
  )
}
